import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RecaptchaBadge from '../../components/RecaptchaBadge';
import {
  APP_PATH_FORGOT_PASSWORD,
  APP_PATH_SIGNUP,
  MAIN_APP_NAME
} from '../../config';
import { useSignIn } from '../../hooks/useSignIn';
import { trackForgotPasswordClick } from '../../helpers';
import usePageTitle from '../../hooks/usePageTitle';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    height: '100vh',
    minHeight: '100%'
  },
  grid: {
    height: '100%'
  },
  quoteContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.background.default}`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  quote: {
    backgroundColor: theme.palette.neutral,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  quoteInner: {
    textAlign: 'center',
    flexBasis: '600px'
  },
  quoteText: {
    color: theme.palette.white,
    fontWeight: 300
  },
  name: {
    marginTop: theme.spacing(3),
    color: theme.palette.white
  },
  bio: {
    color: theme.palette.white
  },
  contentContainer: {
    height: '100%'
  },
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBototm: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  logoImage: {
    marginLeft: theme.spacing(4)
  },
  contentBody: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center'
    }
  },
  form: {
    paddingLeft: 100,
    paddingRight: 100,
    paddingBottom: 125,
    flexBasis: 700,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  title: {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  sugestion: {
    marginTop: theme.spacing(2)
  },
  textField: {
    marginTop: theme.spacing(2)
  },
  signInButton: {
    margin: theme.spacing(2, 0)
  },
  largeLogo: {
    width: theme.spacing(24),
    height: theme.spacing(24)
  }
}));

const SignIn = () => {
  const classes = useStyles();
  const { formState, handleChange, handleBack, handleSignInNormal, hasError } =
    useSignIn();
  usePageTitle('Sign In');

  return (
    <div className={classes.root}>
      <Grid className={classes.grid} container justifyContent="center">
        <Grid className={classes.quoteContainer} item lg={5}>
          <div className={classes.quote}>
            <Typography
              variant="h1"
              color="textPrimary"
              style={{ fontSize: '3rem', fontWeight: 700 }}>
              {MAIN_APP_NAME}
            </Typography>
          </div>
          <div className={classes.quote}>
            <div className={classes.quoteInner}>
              <Typography className={classes.title} variant="h1">
                Where Talent Meets Opportunity. Work Remotely.
              </Typography>
            </div>
          </div>
        </Grid>
        <Grid className={classes.content} item lg={7} xs={12}>
          <div className={classes.content}>
            <div className={classes.contentHeader}>
              <IconButton data-test="go-back-login" onClick={handleBack}>
                <ArrowBackIcon />
              </IconButton>
            </div>
            <div className={classes.contentBody}>
              <form
                className={classes.form}
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSignInNormal(formState);
                }}>
                <Typography className={classes.title} variant="h2" gutterBottom>
                  Welcome Back
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  Access your jobs, applications, and opportunities
                </Typography>
                <TextField
                  data-test="email-login"
                  className={classes.textField}
                  error={hasError('email')}
                  fullWidth
                  helperText={
                    hasError('email') ? formState.errors.email[0] : null
                  }
                  label="Email address"
                  name="email"
                  onChange={handleChange}
                  type="text"
                  value={formState.values.email || ''}
                  variant="outlined"
                  autoComplete="username"
                />
                <TextField
                  data-test="password-login"
                  className={classes.textField}
                  error={hasError('password')}
                  fullWidth
                  helperText={
                    hasError('password') ? formState.errors.password[0] : null
                  }
                  label="Password"
                  name="password"
                  onChange={handleChange}
                  type="password"
                  value={formState.values.password || ''}
                  variant="outlined"
                  autoComplete="current-password"
                />
                <Typography
                  color="textSecondary"
                  variant="body2"
                  style={{ marginTop: 8, textAlign: 'right' }}>
                  <Link
                    component={RouterLink}
                    data-test="forgot-password-link"
                    to={APP_PATH_FORGOT_PASSWORD}
                    variant="body2"
                    onClick={() => trackForgotPasswordClick('sign_in_page')}>
                    Forgot password?
                  </Link>
                </Typography>
                <Button
                  data-test="sing-in-login"
                  className={classes.signInButton}
                  color="primary"
                  disabled={!formState.isValid}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained">
                  Sign In
                </Button>
                <Typography color="textSecondary" variant="body1">
                  New to Remote Job?{' '}
                  <Link
                    data-test="sing-up-login"
                    component={RouterLink}
                    to={APP_PATH_SIGNUP}
                    variant="h6">
                    Create an account
                  </Link>
                </Typography>
                <RecaptchaBadge />
              </form>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default SignIn;
